import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { FC, Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import dayjs from 'dayjs';

import API from '@api/API';

import { ProfileLink } from '@queries/EntertainerQueries';
import {
  CurrentPromoter,
  Promoter,
  useCurrentPromoterQuery,
  usePromoterByBookingHandleQuery,
  usePromoterManageQuery,
} from '@queries/PromoterQueries';
import { useCurrentUser, useUserConversationsQuery } from '@queries/UserQueries';

import { useUpdatePromoterMutation } from '@mutations/PromoterMutations';

import { useCurrentPromoter } from '@hooks/PromoterHooks/useCurrentPromoter';
import { useAnalytics } from '@hooks/useAnalytics';
import { useCurrentUserCity, useCurrentUserLocation } from '@hooks/useCurrentUserLocation';
import { useDailyLocalStorageReminderIsVisible } from '@hooks/useDailyLocalStorageReminderIsVisible';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard, { PromoterHeader } from '@components/cards/EventCard';
import { Form } from '@components/form/Form';
import { Input } from '@components/form/Input';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { DangerBanner } from '@components/layout/DangerBanner/DangerBanner';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { EntertainersList } from '@components/layout/Lists/EntertainersList';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { PopoverActionList } from '@components/layout/PopoverActionList/PopoverActionList';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import {
  ProfileHeader,
  ProfileHeaderPerformanceMetric,
} from '@components/profile/ProfileHeader/ProfileHeader';
import { ProfileLinksSection } from '@components/profile/ProfileSocialLinksSection/ProfileSocialLinksSection';

import { ShowsList } from '@screens/CommunityScreen/components/ShowsList';
import { useShowsGroupedBySectionTitle } from '@screens/event/EventRsvpScreen/hooks/useShowsGroupedBySectionTitle';

import { EventbriteCard } from './EventbriteCard';
import './ManagePromoterProfileScreen.scss';
import { OpenBookingCard } from './OpenBookingCard';
import { PromoterForm } from './PromoterForm';
import { WerqWithUsLink } from './WerqWithUsLink';

export const EventbriteSection = () => {
  const { promoter } = useCurrentPromoterQuery();

  return (
    <>
      {promoter ? (
        <>
          <TitleToolbar text="Connected!" />
          <Card>
            <Card.Header title={promoter?.title} />
            <Card.Body>
              <p>
                Your Eventbrite organizer profile is connected. There's nothing left for you to do.
              </p>
            </Card.Body>
          </Card>
        </>
      ) : (
        <EventbriteCard />
      )}
    </>
  );
};

export const InstagramPost: FC<{
  sourceLogWithImage: any;
  onResizeComplete: () => void;
  onImageLoadError: () => void;
}> = ({ sourceLogWithImage, onResizeComplete, onImageLoadError }) => {
  const [imageDidLoad, setImageDidLoad] = useState(false);
  const [didCaptureTargetHeight, setDidCaptureTargetHeight] = useState(false);
  const [targetHeight, setTargetHeight] = useState<number>();
  const [maxCharacterCount, setMaxCharacterCount] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (imageDidLoad && containerRef.current?.clientHeight) {
      setTargetHeight(containerRef.current.clientHeight);
      setDidCaptureTargetHeight(true);
    }
  }, [imageDidLoad]);

  useLayoutEffect(() => {
    if (didCaptureTargetHeight && targetHeight) {
      if (containerRef.current?.clientHeight > targetHeight) {
        setMaxCharacterCount((prev) => prev - 5);
      } else {
        onResizeComplete();
      }
    }
  }, [didCaptureTargetHeight, targetHeight, maxCharacterCount]);

  return (
    <div key={sourceLogWithImage._id} ref={containerRef} className="ig-post d-flex gap-2">
      <div className="ig-post_img-wrapper">
        <AsyncImage
          src={sourceLogWithImage.imageUrl}
          alt="instagram"
          onLoad={() => setImageDidLoad(true)}
          onError={onImageLoadError}
        />
      </div>
      <div>
        {didCaptureTargetHeight && (
          <div className="ig-post_description-wrapper">
            {/* <span>
              <strong>
                {`Posted ${dayjs(sourceLogWithImage?.postForProcessing?.postedAt).format('M/D/YY')}`}
              </strong>
            </span> */}
            {mapStringToParagraphs(
              `${sourceLogWithImage?.postForProcessing?.text?.substring(0, maxCharacterCount)}...`,
            )}
          </div>
        )}
        <div className="d-flex gap-2">
          <Button variant={sourceLogWithImage.hasEvent ? 'outlined' : 'default'}>
            {sourceLogWithImage.hasEvent ? 'Manage Event' : 'Create Event'}
          </Button>
          <Button variant="outlined" color="neutral">
            Ignore
          </Button>
        </div>
      </div>
    </div>
  );
};

const PromoterLinksSection = ({ promoter }: { promoter: Pick<Promoter, '_id' | 'contact'> }) => {
  const { promoters } = useCurrentPromoter();
  const { updatePromoterAsync } = useUpdatePromoterMutation();
  const isCurrentEntertainer = promoters.some(({ _id }) => promoter._id === _id);

  const entertainerLinks = useMemo<(ProfileLink & { key?: string })[]>(() => {
    const links = [
      promoter.contact?.venmo && {
        type: 'venmo',
        text: 'Venmo',
        url: `venmo://users/${promoter.contact.venmo}`,
      },
      promoter.contact?.instagram && {
        type: 'instagram',
        text: 'Instagram',
        url: `https://instagram.com/${promoter.contact?.instagram}`,
      },
      promoter.contact?.tiktok && {
        type: 'tiktok',
        text: 'TikTok',
        url: `https://tiktok.com/@${promoter.contact.tiktok}`,
      },
      ...(promoter.contact?.links ?? []).map((link, i) => ({
        ...link,
        key: `custom-link-${i}`,
      })),
      promoter.contact?.email && {
        type: 'email',
        text: 'Email',
        url: `mailto:${promoter?.contact?.email}`,
      },
      promoter.contact?.website && {
        type: 'website',
        text: 'Website',
        url: promoter?.contact?.website,
      },
    ];

    return links.filter(Boolean) as (ProfileLink & { key?: string })[];
  }, [promoter]);

  const handleSubmitUpdatedLinks = async (links: ProfileLink[]) => {
    try {
      const updatedLinks = [...(promoter?.contact?.links ?? []), ...links];

      await updatePromoterAsync({ promoterId: promoter._id, contact: { links: updatedLinks } });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const handleRemoveLink = async (link: ProfileLink) => {
    const updatedLinks = promoter?.contact?.links?.filter(({ url }: any) => url !== link.url);

    await updatePromoterAsync({ promoterId: promoter._id, contact: { links: updatedLinks } });
  };

  return (
    <ProfileLinksSection
      canAddLinks={isCurrentEntertainer}
      links={entertainerLinks}
      onSubmitUpdatedLinks={handleSubmitUpdatedLinks}
      onRemoveLink={handleRemoveLink}
    />
  );
};

const INSTAGRAM_ENABLED = false;
const ARE_BOOKINGS_ENABLED = false;

const EmptyPromoterProfile = () => {
  return (
    <div className="grid gap-2">
      <div>
        <TitleToolbar text="We're still in beta!" size="md" />
        <p>
          But don't worry! Working with us is totally free at the moment and you can still get a
          promoter account. Tell us a little about yourself below and we'll get back to you ASAP!
        </p>
      </div>
      <Input placeholder="Email Address" />
      <Textarea placeholder="Tell us why you want to promote with Gaggl..." onChange={() => {}} />
      <Button color="neutral">Submit</Button>
    </div>
  );
};

const EmptyPromoterProfileToCome = () => {
  return (
    <div className="grid gap-2">
      <div>
        <TitleToolbar text="We're still in beta!" size="md" />
        <p>
          But don't worry! You'll be able to do free promotions with us soon. Stay tuned for
          updates!
        </p>
      </div>
    </div>
  );
};

const useWerqWithUsLinkBannerIsVisible = (promoterId: string) => {
  const { isDailyLocalStorageReminderVisible, handleDailyLocalStorageReminderAcknowledged } =
    useDailyLocalStorageReminderIsVisible(`WERQWITHUS_LINK_BANNER::${promoterId}`, 1000, 'isoWeek');

  return {
    isWerqWithUsLinkBannerVisible: isDailyLocalStorageReminderVisible,
    handleWerqWithUsLinkBannerAcknowledged: handleDailyLocalStorageReminderAcknowledged,
  };
};

const ManagePromoterSections = () => {
  const { navigate } = useRouter();
  const [hiddenInstagramPosts, setHiddenInstagramPosts] = useState<Record<string, boolean>>({});
  const [currentResizeIndex, setCurrentResizeIndex] = useState(0);

  const { promoter } = useCurrentPromoter();
  const { promoterDetails, promoterDetailsAreReady } = usePromoterManageQuery(promoter?._id);

  const {
    upcomingPromoterEvents,
    upcomingPromoterScheduleDateDetails,
    instagramPosts,
    openBookings,
  } = useMemo(() => {
    const today = dayjs();

    return {
      upcomingPromoterEvents:
        promoterDetails?.events?.filter(
          (event) => dayjs(event.startDate).isAfter(today) || dayjs(event.startDate).isSame(today),
        ) ?? [],
      upcomingPromoterScheduleDateDetails: [],
      instagramPosts:
        promoterDetails?.instagramPosts?.filter(
          (sourceLogWithImage) => !hiddenInstagramPosts[sourceLogWithImage._id],
        ) ?? [],
      openBookings: promoterDetails?.bookingOpportunities ?? [],
    };
  }, [promoterDetails, hiddenInstagramPosts]);

  const handleCreateBookingOpportunity = () => {
    navigate('/open-bookings/create-edit');
  };

  const handleResizeComplete = (index: number) => {
    setCurrentResizeIndex(index + 1);
  };

  return (
    <>
      {ARE_BOOKINGS_ENABLED && (
        <>
          <TitleToolbar
            text="Open Bookings 📖"
            size="md"
            action={
              <Button
                variant="flat"
                size="sm"
                startIcon={<FontAwesomeIcon name="plus" />}
                onClick={handleCreateBookingOpportunity}
              >
                Create
              </Button>
            }
          />
          {promoterDetailsAreReady ? (
            <>
              {!!openBookings.length ? (
                <HorizontalScroll
                  items={
                    openBookings.map((openBooking) => (
                      <OpenBookingCard key={openBooking._id} bookingOpportunity={openBooking} />
                    )) ?? []
                  }
                />
              ) : (
                <EmptyState
                  title="No open bookings"
                  text="You don't have any open bookings. Create one here if you want to share your booking opportunity with local entertainers."
                  button={{
                    text: 'Create Open Booking',
                    onClick: handleCreateBookingOpportunity,
                  }}
                />
              )}
            </>
          ) : (
            <Skeleton height={140} />
          )}
        </>
      )}

      {INSTAGRAM_ENABLED && (
        <>
          <TitleToolbar
            text={
              <div className="d-flex align-items-center gap-2">
                <span>Instagram Posts</span>
                <FontAwesomeIcon faStyle="fab" name="instagram" />
              </div>
            }
            size="md"
          />
          <div className="grid gap-4">
            {instagramPosts.slice(0, currentResizeIndex + 1).map((sourceLogWithImage, i) => (
              <InstagramPost
                key={sourceLogWithImage._id}
                sourceLogWithImage={sourceLogWithImage}
                onResizeComplete={() => handleResizeComplete(i)}
                onImageLoadError={() =>
                  setHiddenInstagramPosts({
                    ...hiddenInstagramPosts,
                    [sourceLogWithImage._id]: true,
                  })
                }
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

const WerqWithUsLinkBanner = ({ currentPromoter }: { currentPromoter: CurrentPromoter }) => {
  const { isWerqWithUsLinkBannerVisible, handleWerqWithUsLinkBannerAcknowledged } =
    useWerqWithUsLinkBannerIsVisible(currentPromoter._id);

  return (
    <>
      {isWerqWithUsLinkBannerVisible && !currentPromoter?.linkCopiedAt && (
        <div className="mb-2">
          <DangerBanner
            title={`This is your "werqwith.us" link!`}
            text={
              <div className="d-grid gap-1 mt-1">
                <p className="my-0">
                  {`You know `}
                  <strong>linktree</strong>
                  {`, right? This is like that but for queer producers. Edit your profile to update your tagline and use the `}
                  <strong>"Add Link"</strong>
                  {` button to update your links! `}
                </p>
                <p className="my-0">
                  {`Click the `}
                  <strong>"Share"</strong>
                  {` button to copy your link!`}
                </p>
              </div>
            }
            button={{
              text: 'Got it, thanks!',
              onClick: handleWerqWithUsLinkBannerAcknowledged,
            }}
          />
        </div>
      )}
    </>
  );
};

interface PromoterDetailsScreenProps {
  back?: string;
  bookingHandle: string;
  mode?: 'manage';
}

export const PromoterDetailsScreen = createScreenComponent<PromoterDetailsScreenProps>(
  ({ back: backlinkUrl, bookingHandle, mode }) => {
    const { promoter: selectedPromoter, promoters, changeCurrentPromoter } = useCurrentPromoter();
    const {
      isPromoterReady,
      promoter: bookingHandlePromoter,
      promoterEvents,
      promoterScheduleDateDetails,
    } = usePromoterByBookingHandleQuery(bookingHandle);
    const { navigate, back } = useRouter();

    const promoter = bookingHandle === 'private-profile' ? selectedPromoter : bookingHandlePromoter;
    const currentPromoter = promoters.find(({ _id }) => promoter?._id === _id);
    const isCurrentPromoter = !!currentPromoter;

    const handleCreateEvent = () => {
      navigate('/manage/schedule-submissions');
    };

    const performanceMetrics: ProfileHeaderPerformanceMetric[] = useMemo(() => {
      return [
        {
          count: 0,
          label: 'Shared',
          to: '',
        },
        {
          count: 0,
          label: 'Upcoming',
          to: '',
        },
      ];
    }, []);

    const { hasShows, hasUpcomingShows, showsGroupedBySectionTitle } =
      useShowsGroupedBySectionTitle({
        events: [...(promoterEvents?.upcoming ?? []), ...(promoterEvents?.previous ?? [])],
        scheduleDateDetails: promoterScheduleDateDetails?.upcoming ?? [],
        isCurrentEntertainer: isCurrentPromoter,
      });

    const backUrl = '/manage/promoter';

    const handleAddExternalLink = () => {};

    return (
      <Screen
        name="Promoter Details Screen"
        hideToolbar={mode !== 'manage'}
        headerProps={{
          left: <></>,
          title: <Screen.GagglTitle />,
          // right:
          //   hasUserHasPromoterProfile && !!userConversations.promoter.length ? (
          //     <IconButton
          //       color="neutral"
          //       variant="smooth"
          //       badge={userConversations.promoter.some(({ isUnread }) => isUnread)}
          //       onClick={() => {
          //         navigate('/conversations/promoter', {
          //           state: { back: '/manage/promoter' },
          //         });
          //       }}
          //     >
          //       <FontAwesomeIcon name="paper-plane" />
          //     </IconButton>
          //   ) : undefined,
        }}
      >
        <Screen.Content maxWidth={425}>
          {isPromoterReady && promoter ? (
            <Fragment key={promoter._id}>
              <div className="grid">
                <div style={{}}>
                  {bookingHandle && (
                    <div className="mb-3">
                      {currentPromoter && (
                        <WerqWithUsLinkBanner currentPromoter={currentPromoter} />
                      )}
                      <WerqWithUsLink
                        isCurrentPromoter={isCurrentPromoter}
                        promoterId={promoter?._id}
                        bookingHandle={promoter?.bookingHandle ?? 'private-profile'}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <ProfileHeader
                    key={`promoter-avatar-${promoter?.avatarUrl}`}
                    isClaimed
                    avatarUrl={promoter?.avatarUrl ?? ''}
                    title={promoter.title}
                    performanceMetrics={performanceMetrics}
                    action={
                      isCurrentPromoter &&
                      promoters.length > 1 && (
                        <PopoverActionList
                          buttons={promoters.map((promoter) => ({
                            text: promoter.title,
                            onClick: () => changeCurrentPromoter(promoter._id),
                          }))}
                        >
                          <IconButton variant="smooth" color="neutral">
                            <FontAwesomeIcon name="chevron-down" />
                          </IconButton>
                        </PopoverActionList>
                      )
                    }
                  />

                  {!!promoter?.description && (
                    <div className="mt-1">
                      {promoter.description.split('\n').map((paragraph, index) => (
                        <p key={index} className="m-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="mt-2">
                    <PromoterLinksSection promoter={promoter} />
                  </div>

                  {!!promoter?.entertainers?.length && (
                    <>
                      <TitleToolbar text="Performers" size="sm" />
                      <EntertainersList entertainers={promoter?.entertainers} />
                    </>
                  )}

                  {isCurrentPromoter && (
                    <div className="mt-3">
                      <Button
                        className="py-2 px-3"
                        roundness="rounded"
                        variant="smooth"
                        color="neutral"
                        onClick={() => navigate('/manage/promoter/edit')}
                      >
                        Edit Profile
                      </Button>
                    </div>
                  )}
                </div>

                <>
                  {!hasUpcomingShows && (
                    <div className="mt-4">
                      {isCurrentPromoter ? (
                        <EmptyState
                          title="No upcoming shows"
                          text="You aren't sharing any upcoming shows. Add a show to help the community support you."
                          button={{
                            color: 'primary',
                            roundness: 'rounded',
                            text: 'Manage public calendar',
                            onClick: handleCreateEvent,
                            badge: 1,
                          }}
                        />
                      ) : (
                        <EmptyState
                          title="No upcoming shows"
                          text={`${promoter.title} has not listed any upcoming shows.`}
                        />
                      )}
                    </div>
                  )}

                  {hasShows && (
                    <ShowsList
                      className="mt-4"
                      hideEntertainerMenuButton
                      showsGroupedBySectionTitle={showsGroupedBySectionTitle}
                      cityId={promoter?.cityIds?.[0]}
                      back={backUrl}
                      onAddExternalLink={isCurrentPromoter ? handleAddExternalLink : undefined}
                    />
                  )}
                </>
              </div>
            </Fragment>
          ) : (
            <>
              <Skeleton height={140} />
              <Skeleton height={140} />
              <Skeleton height={140} />
            </>
          )}
        </Screen.Content>
      </Screen>
    );
  },
);

const PromoterOnboardingContent = () => {
  const [welcomeMessageIsVisible, setWelcomeMessageIsVisible] = useState(true);
  const { currentCity, cities = [] } = useCurrentUserCity();

  return (
    <>
      {welcomeMessageIsVisible ? (
        <>
          <TitleToolbar text="Enhance Your Event Planning Experience" size="md" />
          <p>
            As a Gaggl member, you already know the value of discovering amazing events. If
            you&apos;re an event organizer, maximize our platform&apos;s potential! Share gig
            opportunities with talented entertainers, promote your events, and manage booking
            requests seamlessly.
          </p>
          <p>
            Gaggl helps you connect with performers, streamline your event planning, and create
            unforgettable experiences. Enhance your profile today and elevate your event planning to
            the next level!
          </p>
          <Button onClick={() => setWelcomeMessageIsVisible(false)}>Create Producer Profile</Button>
        </>
      ) : (
        <PromoterForm currentCity={currentCity} cities={cities} />
      )}
    </>
  );
};

interface ManagePromoterProfileScreenProps {
  back?: string;
}

export const ManagePromoterProfileScreen = createScreenComponent<ManagePromoterProfileScreenProps>(
  (props) => {
    const { promoter } = useCurrentPromoter();

    const bookingHandle = promoter?.bookingHandle ?? 'private-profile';

    return <PromoterDetailsScreen {...props} bookingHandle={bookingHandle} mode="manage" />;
  },
);
